import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { FileText, FolderClosed, Search } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtIDR } from "@/lib/constants";
import InvoiceViewer from "@/components/InvoiceViewer";
import { Input } from "@/components/ui/input";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";

export default function Invoices() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState(null);
  const [detail, setDetail] = useState(null);
  const [denied, setDenied] = useState(false);
  const [search, setSearch] = useState("");
  const [viewInvoice, setViewInvoice] = useState(null);
  const [tab, setTab] = useState("active");
  const [archive, setArchive] = useState([]);
  const [openBatch, setOpenBatch] = useState(null);
  const [history, setHistory] = useState([]);
  const [batches, setBatches] = useState([]);

  const { user } = useAuth();
  const isOwner = user.role === "owner";

  const load = useCallback(async () => {
    try {
      const [inv, arc, bat] = await Promise.all([
        api.get("/invoices", { params: { latest_only: true } }),
        api.get("/batches/closed-archive"),
        api.get("/batches"),
      ]);
      setInvoices(inv.data);
      setArchive(arc.data);
      setBatches(bat.data);
    } catch (e) {
      if (e?.response?.status === 403) {
        setDenied(true);
      } else {
        toast.error(apiError(e));
      }
      setInvoices([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = async (iv) => {
    setDetail(iv);
    setHistory([]);
    if (isOwner) {
      try {
        const { data } = await api.get(`/orders/${iv.order_id}/invoices`);
        setHistory(data);
      } catch {
        setHistory([]);
      }
    }
  };

  const archivedBatchIds = new Set(archive.map((b) => b.id));
  const activeBatchIds = new Set((batches || []).filter((b) => b.status !== "closed").map((b) => b.id));
  const groupOf = (iv) =>
    activeBatchIds.has(iv.po_batch_id) ? "active" : archivedBatchIds.has(iv.po_batch_id) ? "archive" : "unlinked";
  const q = search.trim().toLowerCase();
  const matches = (iv) =>
    !q ||
    iv.customer_name.toLowerCase().includes(q) ||
    (iv.batch_name || "").toLowerCase().includes(q) ||
    (iv.invoice_number || "").toLowerCase().includes(q) ||
    (iv.customer_phone || "").toLowerCase().includes(q) ||
    iv.order_id.toLowerCase().includes(q);
  const scoped = (invoices || []).filter((iv) =>
    tab === "unlinked"
      ? groupOf(iv) === "unlinked"
      : tab === "active"
        ? groupOf(iv) === "active"
        : groupOf(iv) === "archive" && (openBatch ? iv.po_batch_id === openBatch.id : true)
  );
  const filteredInvoices = scoped.filter(matches);

  return (
    <div data-testid="invoices-page">
      <div className="mb-5">
        <h1 className="font-heading text-2xl font-extrabold tracking-tight">Final Invoices</h1>
        <p className="text-sm text-muted-foreground">One current invoice per customer order — previous versions stay in the audit trail</p>
      </div>

      <div className="mb-3 flex gap-2">
        <button
          data-testid="invoices-tab-active"
          onClick={() => { setTab("active"); setOpenBatch(null); }}
          className={`h-11 flex-1 rounded-md border text-sm font-semibold ${tab === "active" ? "border-primary bg-primary/5 text-primary" : "bg-card text-stone-600"}`}
        >
          Active Batches
        </button>
        <button
          data-testid="invoices-tab-archive"
          onClick={() => { setTab("archive"); setOpenBatch(null); }}
          className={`h-11 flex-1 rounded-md border text-sm font-semibold ${tab === "archive" ? "border-primary bg-primary/5 text-primary" : "bg-card text-stone-600"}`}
        >
          Closed Archive {archive.length > 0 ? `(${archive.length})` : ""}
        </button>
      </div>

      {tab === "archive" && !openBatch && (
        <div className="mb-3 space-y-2.5" data-testid="closed-archive-list">
          {archive.length === 0 ? (
            <p className="rounded-md border border-dashed py-10 text-center text-sm text-muted-foreground" data-testid="archive-empty">
              No closed batches yet
            </p>
          ) : (
            archive.map((b) => (
              <button
                key={b.id}
                data-testid={`archive-batch-${b.id}`}
                onClick={() => setOpenBatch(b)}
                className="w-full rounded-md border bg-card p-4 text-left transition-transform hover:-translate-y-[1px] active:scale-[0.99]"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="flex items-center gap-1.5 truncate text-sm font-bold">
                      <FolderClosed className="h-4 w-4 shrink-0 text-stone-400" /> {b.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Closed {b.closed_at ? format(new Date(b.closed_at), "d MMM yyyy") : "—"} · {b.total_orders} order(s) · {b.total_invoices} invoice(s)
                    </p>
                  </div>
                </div>
                <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                  <span className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                    Paid {b.paid_count} · {fmtIDR(b.paid_total)}
                  </span>
                  <span className="rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                    Outstanding {b.outstanding_count} · {fmtIDR(b.outstanding_total)}
                  </span>
                </div>
              </button>
            ))
          )}
        </div>
      )}

      {tab === "archive" && openBatch && (
        <button data-testid="archive-back" onClick={() => setOpenBatch(null)} className="mb-3 text-sm font-semibold text-primary">
          ← All closed batches · {openBatch.name}
        </button>
      )}

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
        <Input
          data-testid="invoice-search-input"
          className="h-12 pl-9"
          placeholder="Search customer, phone, invoice no., or batch…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {invoices === null ? (
        <div className="grid place-items-center py-24">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      ) : denied ? (
        <div data-testid="invoices-denied" className="grid place-items-center rounded-md border border-dashed py-20 text-center">
          <p className="font-medium text-muted-foreground">Invoices are only available to Owner, Admin Input, and Admin Checker.</p>
        </div>
      ) : invoices.length === 0 ? (
        <div data-testid="invoices-empty" className="grid place-items-center rounded-md border border-dashed py-20 text-center">
          <FileText className="mb-2 h-8 w-8 text-stone-300" />
          <p className="font-medium text-muted-foreground">No invoices generated yet</p>
          <p className="mt-1 text-xs text-muted-foreground">Invoices are generated from the order detail page</p>
        </div>
      ) : (
        <div className="space-y-2.5" data-testid="invoices-list">
          {filteredInvoices.length === 0 ? (
            <p className="rounded-md border border-dashed py-10 text-center text-sm text-muted-foreground" data-testid="invoices-no-match">
              No invoices match your search
            </p>
          ) : null}
          {filteredInvoices.map((iv) => {
            return (
              <button
                key={iv.id}
                data-testid={`invoice-card-${iv.id}`}
                onClick={() => openDetail(iv)}
                className="w-full rounded-md border bg-card p-4 text-left transition-transform hover:-translate-y-[1px] active:scale-[0.99]"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold">{iv.customer_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {iv.batch_name} · {format(new Date(iv.created_at), "d MMM yyyy, HH:mm")} · by {iv.created_by_name}
                    </p>
                  </div>
                  <span
                    data-testid={`invoice-version-${iv.id}`}
                    className="shrink-0 rounded-md border border-rose-200 bg-rose-50 px-2 py-0.5 text-xs font-bold text-rose-700"
                  >
                    V{iv.version} · LATEST
                  </span>
                </div>
                <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                  <span className="rounded-md border bg-[#EDE4DE] px-2 py-0.5 text-[11px] font-bold text-stone-700">Total {fmtIDR(iv.total)}</span>
                  <span className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                    Payment Received {fmtIDR(iv.paid_verified)}
                  </span>
                  {iv.credit_used > 0 && (
                    <span className="rounded-md border bg-[#EDE4DE] px-2 py-0.5 text-[11px] font-semibold text-stone-600">Credit {fmtIDR(iv.credit_used)}</span>
                  )}
                  <span
                    data-testid={`invoice-balance-${iv.id}`}
                    className={`rounded-md border px-2 py-0.5 text-[11px] font-bold ${
                      iv.balance_due > 0 ? "border-amber-200 bg-amber-50 text-amber-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"
                    }`}
                  >
                    {iv.balance_due > 0 ? `Due ${fmtIDR(iv.balance_due)}` : "PAID"}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <Drawer open={!!detail} onOpenChange={(o) => !o && setDetail(null)} shouldScaleBackground={false}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle className="font-heading">{detail ? `Invoice V${detail.version} — ${detail.customer_name}` : ""}</DrawerTitle>
          </DrawerHeader>
          {detail && (
            <div className="max-h-[75vh] space-y-3 overflow-y-auto px-4 pb-8" data-testid="invoice-detail">
              <div className="rounded-md border bg-card p-3">
                <div className="space-y-1.5" data-testid="invoice-lines">
                  {detail.lines.map((l, i) => (
                    <p key={i} className="flex justify-between text-xs">
                      <span className="min-w-0 truncate font-semibold">
                        {l.product_name}
                        {l.variant ? ` (${l.variant})` : ""} ×{l.quantity}
                      </span>
                      <span className="shrink-0">{l.selling_price != null ? fmtIDR(l.line_total) : "price TBD"}</span>
                    </p>
                  ))}
                </div>
                <div className="mt-2 space-y-1 border-t pt-2 text-xs" data-testid="invoice-totals">
                  <p className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-semibold">{fmtIDR(detail.subtotal)}</span></p>
                  <p className="flex justify-between"><span className="text-muted-foreground">Shipping Fee</span><span className="font-semibold">{fmtIDR(detail.shipping_charge)}</span></p>
                  <p className="flex justify-between"><span className="text-muted-foreground">Insurance</span><span className="font-semibold">{fmtIDR(detail.insurance_amount)}</span></p>
                  {detail.adjustment !== 0 && (
                    <p className="flex justify-between"><span className="text-muted-foreground">Adjustment</span><span className="font-semibold">{fmtIDR(detail.adjustment)}</span></p>
                  )}
                  <p className="flex justify-between border-t pt-1 text-sm"><span className="font-bold">Total</span><span className="font-heading font-extrabold" data-testid="invoice-total">{fmtIDR(detail.total)}</span></p>
                  <p className="flex justify-between"><span className="text-muted-foreground">Payment Received</span><span className="font-semibold text-emerald-700">{fmtIDR(detail.paid_verified)}</span></p>
                  {detail.credit_used > 0 && (
                    <p className="flex justify-between"><span className="text-muted-foreground">Credit applied</span><span className="font-semibold">{fmtIDR(detail.credit_used)}</span></p>
                  )}
                  <p className="flex justify-between text-sm">
                    <span className="font-bold">Balance due</span>
                    <span className={`font-heading font-extrabold ${detail.balance_due > 0 ? "text-amber-700" : "text-emerald-700"}`} data-testid="invoice-balance-due">
                      {fmtIDR(detail.balance_due)}
                    </span>
                  </p>
                </div>
              </div>
              {detail.notes && <p className="rounded-md border bg-accent/40 px-3 py-2 text-xs text-stone-600"><b>Note:</b> {detail.notes}</p>}
              {isOwner && history.length > 1 && (
                <div className="rounded-md border bg-card p-3" data-testid="invoice-history">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-stone-500">Invoice History · Owner only</p>
                  <div className="mt-2 space-y-1">
                    {[...history].reverse().map((h) => (
                      <button
                        key={h.id}
                        data-testid={`invoice-history-${h.version}`}
                        onClick={() => { setViewInvoice(h); setDetail(null); }}
                        className="flex w-full items-center justify-between rounded-md border px-2.5 py-2 text-left text-[11px]"
                      >
                        <span className="font-semibold">
                          V{h.version} {h.version === detail.version ? "· LATEST" : "· superseded"} · {h.invoice_number || "—"}
                        </span>
                        <span className="tabular-nums text-stone-600">{fmtIDR(h.total)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <button
                data-testid="invoice-view-customer-doc"
                onClick={() => { setViewInvoice(detail); setDetail(null); }}
                className="w-full rounded-md border bg-primary px-3 py-2.5 text-sm font-semibold text-white"
              >
                View Customer Invoice
              </button>
              <button
                data-testid="invoice-open-order"
                onClick={() => navigate(`/orders/${detail.order_id}`)}
                className="w-full rounded-md border bg-card px-3 py-2.5 text-sm font-semibold text-primary"
              >
                Open order #{detail.order_id.slice(0, 8)}
              </button>
            </div>
          )}
        </DrawerContent>
      </Drawer>

      <InvoiceViewer invoice={viewInvoice} open={!!viewInvoice} onOpenChange={(o) => !o && setViewInvoice(null)} />
    </div>
  );
}
